/**
 * Print Bridge Service for ESC/POS Thermal Printers
 * 
 * This service provides a bridge between the web application and thermal printers.
 * It supports multiple connection methods:
 * 1. WebSocket to local Print Bridge server (recommended)
 * 2. Direct USB/Serial through WebUSB API (Chrome only)
 * 3. Bluetooth connection via Web Bluetooth API
 * 4. Local network fallback for offline mode
 * 
 * Supports universal ESC/POS commands for all thermal printer brands:
 * - Epson
 * - Star Micronics
 * - Citizen
 * - Bixolon
 * - Generic ESC/POS
 */

import { ESC_POS, generateESCPOSReceipt } from './printing';

// ==================== PRINT BRIDGE CLASS ====================

class PrintBridgeService {
  constructor() {
    this.connected = false;
    this.socket = null;
    this.printers = [];
    this.defaultPrinter = null;
    this.connectionType = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.listeners = new Map();
    this.bluetoothDevice = null;
    this.bluetoothCharacteristic = null;
    this.localServerUrl = null;
  }

  // ==================== CONNECTION METHODS ====================

  /**
   * Connect to local Print Bridge server via WebSocket
   * @param {string} url - WebSocket URL (default: ws://localhost:9100)
   */
  async connectWebSocket(url = 'ws://localhost:9100') {
    return new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(url);
        this.connectionType = 'websocket';
        this.localServerUrl = url;

        this.socket.onopen = () => {
          console.log('Print Bridge: Connected via WebSocket');
          this.connected = true;
          this.reconnectAttempts = 0;
          this.emit('connected', { type: 'websocket' });
          
          // Request printer list
          this.send({ action: 'list_printers' });
          resolve(true);
        };

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (e) {
            console.error('Print Bridge: Invalid message', e);
          }
        };

        this.socket.onclose = () => {
          console.log('Print Bridge: Disconnected');
          this.connected = false;
          this.emit('disconnected');
          this.attemptReconnect(url);
        };

        this.socket.onerror = (error) => {
          console.error('Print Bridge: Connection error', error);
          this.emit('error', { message: 'Connection failed' });
          reject(new Error('WebSocket connection failed'));
        };

        // Timeout for connection
        setTimeout(() => {
          if (!this.connected) {
            reject(new Error('Connection timeout'));
          }
        }, 5000);

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Connect to printer via WebUSB (Chrome only)
   */
  async connectUSB() {
    if (!navigator.usb) {
      throw new Error('WebUSB not supported in this browser');
    }

    try {
      // Request USB device
      const device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: 0x04b8 }, // Epson
          { vendorId: 0x0519 }, // Star Micronics
          { vendorId: 0x1504 }, // Citizen
          { vendorId: 0x1d90 }, // Bixolon
        ]
      });

      await device.open();
      await device.selectConfiguration(1);
      await device.claimInterface(0);

      this.connected = true;
      this.connectionType = 'usb';
      this.usbDevice = device;

      const printerName = `USB: ${device.productName || 'Unknown Printer'}`;
      this.printers = [{ id: 'usb-0', name: printerName, type: 'usb' }];
      this.defaultPrinter = this.printers[0];

      console.log('Print Bridge: Connected via USB', printerName);
      this.emit('connected', { type: 'usb', printer: printerName });

      return true;
    } catch (error) {
      console.error('Print Bridge: USB connection failed', error);
      throw error;
    }
  }

  /**
   * Connect to printer via Web Bluetooth API
   * Supports Bluetooth thermal printers with SPP (Serial Port Profile)
   */
  async connectBluetooth() {
    if (!navigator.bluetooth) {
      throw new Error('Web Bluetooth not supported in this browser. Use Chrome or Edge.');
    }

    try {
      // Request Bluetooth device - looking for printers with Serial Port Profile
      const device = await navigator.bluetooth.requestDevice({
        // Accept all devices and filter manually, or use specific services
        acceptAllDevices: true,
        optionalServices: [
          '00001101-0000-1000-8000-00805f9b34fb', // SPP UUID
          '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Common printer service
          '000018f0-0000-1000-8000-00805f9b34fb', // Another printer service
        ]
      });

      console.log('Print Bridge: Bluetooth device selected:', device.name);
      this.bluetoothDevice = device;

      // Connect to GATT server
      const server = await device.gatt.connect();
      console.log('Print Bridge: Connected to GATT server');

      // Try to find a writable characteristic for printing
      let characteristic = null;
      
      // Try common printer service UUIDs
      const serviceUUIDs = [
        '00001101-0000-1000-8000-00805f9b34fb',
        '49535343-fe7d-4ae5-8fa9-9fafd205e455',
        '000018f0-0000-1000-8000-00805f9b34fb',
      ];

      for (const serviceUUID of serviceUUIDs) {
        try {
          const service = await server.getPrimaryService(serviceUUID);
          const characteristics = await service.getCharacteristics();
          
          // Find a writable characteristic
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              characteristic = char;
              console.log('Print Bridge: Found writable characteristic');
              break;
            }
          }
          if (characteristic) break;
        } catch (e) {
          // Service not available, try next
          continue;
        }
      }

      if (!characteristic) {
        // Fallback: Try to get any service and characteristic
        const services = await server.getPrimaryServices();
        for (const service of services) {
          try {
            const chars = await service.getCharacteristics();
            for (const char of chars) {
              if (char.properties.write || char.properties.writeWithoutResponse) {
                characteristic = char;
                break;
              }
            }
            if (characteristic) break;
          } catch (e) {
            continue;
          }
        }
      }

      if (!characteristic) {
        throw new Error('No writable characteristic found on this Bluetooth device');
      }

      this.bluetoothCharacteristic = characteristic;
      this.connected = true;
      this.connectionType = 'bluetooth';

      const printerName = `Bluetooth: ${device.name || 'Unknown Printer'}`;
      this.printers = [{ id: 'bt-0', name: printerName, type: 'bluetooth' }];
      this.defaultPrinter = this.printers[0];

      // Handle disconnection
      device.addEventListener('gattserverdisconnected', () => {
        console.log('Print Bridge: Bluetooth device disconnected');
        this.connected = false;
        this.connectionType = null;
        this.bluetoothDevice = null;
        this.bluetoothCharacteristic = null;
        this.emit('disconnected');
      });

      console.log('Print Bridge: Connected via Bluetooth', printerName);
      this.emit('connected', { type: 'bluetooth', printer: printerName });

      return true;
    } catch (error) {
      console.error('Print Bridge: Bluetooth connection failed', error);
      throw error;
    }
  }

  /**
   * Disconnect from printer
   */
  async disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    if (this.usbDevice) {
      await this.usbDevice.close();
      this.usbDevice = null;
    }

    if (this.bluetoothDevice && this.bluetoothDevice.gatt.connected) {
      this.bluetoothDevice.gatt.disconnect();
      this.bluetoothDevice = null;
      this.bluetoothCharacteristic = null;
    }

    this.connected = false;
    this.connectionType = null;
    this.printers = [];
    this.defaultPrinter = null;

    this.emit('disconnected');
  }

  // ==================== PRINT METHODS ====================

  /**
   * Print raw ESC/POS commands
   */
  async printRaw(commands, printerId = null) {
    if (!this.connected) {
      throw new Error('Not connected to printer');
    }

    const printer = printerId 
      ? this.printers.find(p => p.id === printerId) 
      : this.defaultPrinter;

    if (!printer) {
      throw new Error('No printer available');
    }

    if (this.connectionType === 'websocket') {
      return this.send({
        action: 'print',
        printer_id: printer.id,
        data: commands,
        encoding: 'base64'
      });
    }

    if (this.connectionType === 'usb') {
      const encoder = new TextEncoder();
      const data = encoder.encode(commands);
      await this.usbDevice.transferOut(1, data);
      return true;
    }

    if (this.connectionType === 'bluetooth') {
      const encoder = new TextEncoder();
      const data = encoder.encode(commands);
      
      // Bluetooth has a max packet size, so we need to chunk the data
      const chunkSize = 512; // Most Bluetooth printers support 512 bytes
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize);
        if (this.bluetoothCharacteristic.properties.writeWithoutResponse) {
          await this.bluetoothCharacteristic.writeValueWithoutResponse(chunk);
        } else {
          await this.bluetoothCharacteristic.writeValue(chunk);
        }
        // Small delay between chunks to prevent buffer overflow
        if (i + chunkSize < data.length) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
      return true;
    }

    throw new Error('Unknown connection type');
  }

  /**
   * Print a receipt
   */
  async printReceipt(order, restaurant, options = {}) {
    const commands = generateESCPOSReceipt(order, restaurant, options);
    return this.printRaw(commands, options.printerId);
  }

  /**
   * Print kitchen ticket
   */
  async printKitchenTicket(order, options = {}) {
    const commands = this.generateKitchenTicketESCPOS(order, options);
    return this.printRaw(commands, options.printerId);
  }

  /**
   * Open cash drawer
   */
  async openCashDrawer(printerId = null) {
    const commands = ESC_POS.INIT + ESC_POS.CASH_DRAWER_KICK;
    return this.printRaw(commands, printerId);
  }

  /**
   * Print test page
   */
  async printTestPage(printerId = null) {
    let commands = '';
    commands += ESC_POS.INIT;
    commands += ESC_POS.ALIGN_CENTER;
    commands += ESC_POS.DOUBLE_SIZE;
    commands += 'PRINT TEST\n';
    commands += ESC_POS.NORMAL_SIZE;
    commands += '\n';
    commands += 'If you can read this,\n';
    commands += 'the printer is working!\n';
    commands += '\n';
    commands += '-'.repeat(32) + '\n';
    commands += `Date: ${new Date().toLocaleString()}\n`;
    commands += '-'.repeat(32) + '\n';
    commands += '\n';
    commands += ESC_POS.FEED_3_LINES;
    commands += ESC_POS.CUT_PAPER_PARTIAL;

    return this.printRaw(commands, printerId);
  }

  /**
   * Print QR Code for table ordering (optimized for thermal printers)
   * @param {Object} table - Table data with table_number, zone, capacity
   * @param {Object} restaurant - Restaurant data with name
   * @param {string} qrUrl - The URL to encode in the QR code
   * @param {Object} options - Print options
   */
  async printTableQR(table, restaurant, qrUrl, options = {}) {
    if (!this.connected) {
      throw new Error('Not connected to printer. Please connect your thermal printer first.');
    }

    const { printerId = null, paperWidth = 80 } = options;
    
    // ESC/POS QR code commands
    // QR Code printing uses GS ( k command
    const qrData = qrUrl;
    const qrSize = paperWidth === 58 ? 6 : 8; // Module size based on paper width
    
    let commands = '';
    
    // Initialize
    commands += ESC_POS.INIT;
    
    // Center align
    commands += ESC_POS.ALIGN_CENTER;
    
    // Restaurant name (large)
    commands += ESC_POS.DOUBLE_SIZE;
    commands += (restaurant?.name || 'Restaurant') + '\n';
    commands += ESC_POS.NORMAL_SIZE;
    commands += '\n';
    
    // Decorative border
    commands += '='.repeat(paperWidth === 58 ? 24 : 32) + '\n';
    
    // Table number (very large)
    commands += ESC_POS.DOUBLE_SIZE;
    commands += `TABLE ${table.table_number}\n`;
    commands += ESC_POS.NORMAL_SIZE;
    
    // Zone and capacity
    if (table.zone) {
      commands += `${table.zone}`;
      if (table.capacity) {
        commands += ` - ${table.capacity} seats`;
      }
      commands += '\n';
    }
    
    commands += '\n';
    
    // QR Code using ESC/POS commands
    // GS ( k pL pH cn fn n1 n2 d1...dk - QR Code commands
    
    // Function 165: Select model (Model 2)
    commands += '\x1D\x28\x6B\x04\x00\x31\x41\x32\x00';
    
    // Function 167: Set module size (qrSize)
    commands += `\x1D\x28\x6B\x03\x00\x31\x43${String.fromCharCode(qrSize)}`;
    
    // Function 169: Set error correction (L=48, M=49, Q=50, H=51)
    commands += '\x1D\x28\x6B\x03\x00\x31\x45\x31'; // M level
    
    // Function 180: Store QR data
    const dataLen = qrData.length + 3;
    const pL = dataLen % 256;
    const pH = Math.floor(dataLen / 256);
    commands += `\x1D\x28\x6B${String.fromCharCode(pL)}${String.fromCharCode(pH)}\x31\x50\x30${qrData}`;
    
    // Function 181: Print QR code
    commands += '\x1D\x28\x6B\x03\x00\x31\x51\x30';
    
    commands += '\n\n';
    
    // Call to action
    commands += ESC_POS.BOLD_ON;
    commands += 'Scan to Order\n';
    commands += ESC_POS.BOLD_OFF;
    
    commands += '\n';
    commands += '='.repeat(paperWidth === 58 ? 24 : 32) + '\n';
    
    // Feed and cut
    commands += ESC_POS.FEED_3_LINES;
    commands += ESC_POS.CUT_PAPER_PARTIAL;
    
    return this.printRaw(commands, printerId);
  }

  /**
   * Print multiple table QR codes
   */
  async printAllTableQRs(tables, restaurant, baseUrl, options = {}) {
    if (!this.connected) {
      throw new Error('Not connected to printer. Please connect your thermal printer first.');
    }

    const activeTables = tables.filter(t => t.is_active);
    
    for (let i = 0; i < activeTables.length; i++) {
      const table = activeTables[i];
      const qrUrl = `${baseUrl}/table-order/${restaurant.id}?table=${table.id}`;
      
      try {
        await this.printTableQR(table, restaurant, qrUrl, options);
        // Small delay between prints to prevent buffer overflow
        if (i < activeTables.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Failed to print QR for table ${table.table_number}:`, error);
        throw error;
      }
    }
    
    return true;
  }

  // ==================== KITCHEN TICKET ESC/POS ====================

  generateKitchenTicketESCPOS(order, options = {}) {
    const { station = null, priority = 'normal' } = options;
    
    let commands = '';
    commands += ESC_POS.INIT;
    
    // Priority header
    if (priority === 'rush') {
      commands += ESC_POS.ALIGN_CENTER;
      commands += ESC_POS.DOUBLE_SIZE;
      commands += '*** RUSH ***\n';
      commands += ESC_POS.NORMAL_SIZE;
    }
    
    // Order number (large)
    commands += ESC_POS.ALIGN_CENTER;
    commands += ESC_POS.DOUBLE_SIZE;
    commands += `#${(order.id || '').slice(-6).toUpperCase()}\n`;
    commands += ESC_POS.NORMAL_SIZE;
    
    // Time
    const time = new Date(order.created_at || new Date()).toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit'
    });
    commands += `${time}\n`;
    
    commands += '\n' + '='.repeat(32) + '\n';
    
    // Table/Order type
    commands += ESC_POS.DOUBLE_HEIGHT;
    if (order.table_number) {
      commands += ESC_POS.ALIGN_CENTER;
      commands += `TABLE ${order.table_number}\n`;
    } else if (order.order_type === 'takeaway') {
      commands += ESC_POS.ALIGN_CENTER;
      commands += 'TAKEAWAY\n';
    } else if (order.order_type === 'delivery') {
      commands += ESC_POS.ALIGN_CENTER;
      commands += 'DELIVERY\n';
    }
    commands += ESC_POS.NORMAL_SIZE;
    
    // Station
    if (station) {
      commands += ESC_POS.BOLD_ON;
      commands += `[${station.toUpperCase()}]\n`;
      commands += ESC_POS.BOLD_OFF;
    }
    
    commands += '='.repeat(32) + '\n\n';
    
    // Allergy alert
    if (order.allergies?.length || order.allergy_info?.length) {
      commands += ESC_POS.BOLD_ON;
      commands += ESC_POS.ALIGN_CENTER;
      commands += '!!! ALLERGY ALERT !!!\n';
      commands += (order.allergies || order.allergy_info).join(', ') + '\n';
      commands += ESC_POS.BOLD_OFF;
      commands += '\n';
    }
    
    // Items
    commands += ESC_POS.ALIGN_LEFT;
    let items = order.items || [];
    if (station) {
      items = items.filter(i => i.station === station);
    }
    
    items.forEach(item => {
      commands += ESC_POS.BOLD_ON;
      commands += `${item.quantity}x ${item.name}\n`;
      commands += ESC_POS.BOLD_OFF;
      
      if (item.modifiers?.length) {
        item.modifiers.forEach(mod => {
          commands += `   + ${mod}\n`;
        });
      }
      
      if (item.notes) {
        commands += `   > ${item.notes}\n`;
      }
      
      commands += '\n';
    });
    
    // Order notes
    if (order.notes) {
      commands += '-'.repeat(32) + '\n';
      commands += 'NOTE: ' + order.notes + '\n';
    }
    
    // Customer name
    if (order.customer_name) {
      commands += '-'.repeat(32) + '\n';
      commands += `Customer: ${order.customer_name}\n`;
    }
    
    commands += ESC_POS.FEED_3_LINES;
    commands += ESC_POS.CUT_PAPER_PARTIAL;
    
    return commands;
  }

  // ==================== HELPER METHODS ====================

  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
      return true;
    }
    return false;
  }

  handleMessage(data) {
    switch (data.type) {
      case 'printers':
        this.printers = data.printers || [];
        if (this.printers.length > 0 && !this.defaultPrinter) {
          this.defaultPrinter = this.printers[0];
        }
        this.emit('printers', this.printers);
        break;

      case 'print_success':
        this.emit('print_success', data);
        break;

      case 'print_error':
        this.emit('print_error', data);
        break;

      case 'status':
        this.emit('status', data);
        break;

      default:
        console.log('Print Bridge: Unknown message', data);
    }
  }

  attemptReconnect(url) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Print Bridge: Reconnecting (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      setTimeout(() => {
        this.connectWebSocket(url).catch(() => {});
      }, 3000);
    }
  }

  // ==================== EVENT HANDLING ====================

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          console.error('Print Bridge: Event handler error', e);
        }
      });
    }
  }

  // ==================== STATUS METHODS ====================

  isConnected() {
    return this.connected;
  }

  getConnectionType() {
    return this.connectionType;
  }

  getPrinters() {
    return this.printers;
  }

  getDefaultPrinter() {
    return this.defaultPrinter;
  }

  setDefaultPrinter(printerId) {
    const printer = this.printers.find(p => p.id === printerId);
    if (printer) {
      this.defaultPrinter = printer;
      return true;
    }
    return false;
  }
}

// Singleton instance
const printBridge = new PrintBridgeService();

export { PrintBridgeService };
export default printBridge;
